import { useState, useCallback } from 'react';
import { ResultsView } from './ResultsView';
import { DebugView } from './DebugView';
import { LiveCamera } from './LiveCamera';

type AppState = 'intro' | 'camera' | 'gallery' | 'analyzing' | 'results' | 'error';

interface GalleryUploadState {
  testStripFile: File | null;
}

export function App() {
  const [state, setState] = useState<AppState>('intro');
  const [result, setResult] = useState<any>(null);
  const [elapsed, setElapsed] = useState<number | null>(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [galleryUpload, setGalleryUpload] = useState<GalleryUploadState>({
    testStripFile: null,
  });

  const handleCameraCapture = useCallback(async (frames: Blob[]) => {
    setState('analyzing');
    const start = performance.now();

    try {
      const form = new FormData();
      frames.forEach((blob, i) => {
        form.append('files', blob, `frame_${i}.jpg`);
      });

      const res = await fetch('/api/analyze-multi', { method: 'POST', body: form });
      const data = await res.json();
      const time = (performance.now() - start) / 1000;
      setElapsed(time);

      if (data.success) {
        setResult(data);
        setState('results');
      } else {
        setErrorMsg(data.error || 'Analyse fehlgeschlagen.');
        setState('error');
      }
    } catch (err: any) {
      setErrorMsg(err.message || 'Netzwerkfehler.');
      setState('error');
    }
  }, []);

  const handleGalleryAnalyze = useCallback(async () => {
    if (!galleryUpload.testStripFile) {
      setErrorMsg('Bitte Teststreifen-Foto auswählen.');
      setState('error');
      return;
    }

    setState('analyzing');
    const start = performance.now();

    try {
      const form = new FormData();
      form.append('test_strip', galleryUpload.testStripFile);

      const res = await fetch('/api/analyze-with-reference', { method: 'POST', body: form });
      const data = await res.json();
      const time = (performance.now() - start) / 1000;
      setElapsed(time);

      if (data.success) {
        setResult(data);
        setState('results');
        setGalleryUpload({ testStripFile: null });
      } else {
        setErrorMsg(data.error || 'Analyse fehlgeschlagen.');
        setState('error');
      }
    } catch (err: any) {
      setErrorMsg(err.message || 'Netzwerkfehler.');
      setState('error');
    }
  }, [galleryUpload]);

  const handleGalleryFileChange = useCallback((field: 'testStripFile', file: File) => {
    setGalleryUpload(prev => ({ ...prev, [field]: file }));
  }, []);

  if (state === 'camera') {
    return <LiveCamera onCapture={handleCameraCapture} onClose={() => setState('intro')} />;
  }

  return (
    <div className="app">
      <header className="header">
        <h1>💧 Wassertest</h1>
        <p className="subtitle">16-in-1 KI-Analyse</p>
      </header>

      {state === 'intro' && (
        <div className="intro">
          <div className="intro-card">
            <h2>Neue Messung</h2>
            <p className="intro-text">
              Teststreifen fotografieren oder hochladen.
              Die KI vergleicht mit der fest hinterlegten Referenzkarte und analysiert alle 16 Wasserwerte automatisch.
            </p>
            <div className="intro-buttons">
              <button className="btn-primary" onClick={() => setState('camera')}>
                📷 Kamera
              </button>
              <button className="btn-secondary" onClick={() => setState('gallery')}>
                🖼️ Aus Galerie
              </button>
            </div>
          </div>
        </div>
      )}

      {state === 'gallery' && (
        <div className="intro">
          <div className="intro-card">
            <h2>Foto hochladen</h2>
            <p className="intro-text">
              Teststreifen-Foto auswählen. Die Referenzkarte wird automatisch mitgeschickt.
            </p>
            <div className="gallery-upload">
              <div className="upload-field">
                <label className="upload-label">
                  <span>📸 Teststreifen</span>
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleGalleryFileChange('testStripFile', file);
                    }}
                    required
                  />
                  <span className="file-name">
                    {galleryUpload.testStripFile ? galleryUpload.testStripFile.name : 'Datei wählen…'}
                  </span>
                </label>
              </div>
            </div>
            <div className="gallery-actions">
              <button className="btn-secondary" onClick={() => setState('intro')}>
                ← Zurück
              </button>
              <button className="btn-primary" onClick={handleGalleryAnalyze} disabled={!galleryUpload.testStripFile}>
                🔬 Analysieren
              </button>
            </div>
          </div>
        </div>
      )}

      {state === 'analyzing' && (
        <div className="analyzing">
          <div className="spinner" />
          <p>🤖 KI analysiert das Wasser…</p>
          <p className="small hint">Mehrstufige Analyse mit {elapsed ? '' : 'Multi-Frame-'}Vision-KI. 15–30 Sekunden.</p>
        </div>
      )}

      {state === 'results' && result?.success && result?.response && (
        <>
          <div className="quality-banner ok">
            <strong>✅ Analyse abgeschlossen</strong>
            {elapsed && <span className="small"> · {elapsed.toFixed(1)}s</span>}
            {result.model && <span className="small"> · {result.model}</span>}
            {result.pipeline && <span className="small"> · {result.pipeline}</span>}
          </div>
          <ResultsView results={result.response.results || []} />
          <div className="actions">
            <button className="btn-secondary" onClick={() => setState('intro')}>
              ← Neue Messung
            </button>
            <button className="btn-secondary" onClick={() => setState('results')}>
              {result.debug_overlay ? '🔍 Debug' : ''}
            </button>
          </div>
          <DebugView result={result.response} onBack={() => setState('intro')} />
        </>
      )}

      {state === 'error' && (
        <div className="error-state">
          <p>❌ {errorMsg}</p>
          <button className="btn-primary" onClick={() => setState('intro')}>
            ← Zurück
          </button>
        </div>
      )}
    </div>
  );
}
